import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './InstructorDashboard.css';

const InstructorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'sub-admin';
  const [analytics, setAnalytics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [courseAnalytics, setCourseAnalytics] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [totalInstructors, setTotalInstructors] = useState(0);
  const [activeInstructors, setActiveInstructors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('courses');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isAdmin) {
          const response = await api.get('/admin/users?role=instructor&limit=100');
          const list = response.data.users || [];
          setInstructors(list);
          setTotalInstructors(parseInt(response.data.total || list.length, 10));
          setActiveInstructors(list.filter((inst) => inst.is_active).length);
        } else {
          const [analyticsRes, summaryRes] = await Promise.all([
            api.get('/analytics/instructor'),
            api.get('/analytics/instructor/summary'),
          ]);
          setAnalytics(analyticsRes.data);
          setSummary(summaryRes.data);
        }
      } catch (error) {
        console.error('Error fetching instructor data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  const fetchStudents = useCallback(async () => {
    try {
      const courses = analytics?.course_performance || [];
      if (courses.length === 0) return;

      const courseId = selectedCourse || courses[0]?.id;
      if (!courseId) return;

      const response = await api.get(`/admin/reports/student-performance?course_id=${courseId}`);
      setStudents(response.data.report || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  }, [analytics, selectedCourse]);

  const fetchCourseAnalytics = useCallback(async () => {
    try {
      const courses = analytics?.course_performance || [];
      if (courses.length === 0) return;

      const courseId = selectedCourse || courses[0]?.id;
      if (!courseId) return;

      const response = await api.get(`/analytics/course/${courseId}`);
      setCourseAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching course analytics:', error);
    }
  }, [analytics, selectedCourse]);

  useEffect(() => {
    if (activeTab === 'students' && analytics) {
      fetchStudents();
    }
    if (activeTab === 'analytics' && analytics) {
      fetchCourseAnalytics();
    }
  }, [activeTab, analytics, fetchStudents, fetchCourseAnalytics]);

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      await api.put(`/admin/users/${id}`, { is_active: !currentStatus });
      setInstructors((prev) =>
        prev.map((inst) =>
          inst.id === id ? { ...inst, is_active: !currentStatus } : inst
        )
      );
    } catch (error) {
      console.error('Error updating instructor:', error);
      alert('Unable to update instructor status.');
    }
  };

  const toggleAccordion = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredInstructors = instructors.filter((instructor) => {
    const term = searchTerm.toLowerCase();
    return (
      instructor.first_name?.toLowerCase().includes(term) ||
      instructor.last_name?.toLowerCase().includes(term) ||
      instructor.email?.toLowerCase().includes(term)
    );
  });

  const filteredCourses = (analytics?.course_performance || []).filter((course) => {
    const term = searchTerm.toLowerCase();
    return course.title?.toLowerCase().includes(term);
  });

  const renderInstructorAccordion = () => (
    <div className="course-accordion-container">
      {filteredInstructors.length === 0 ? (
        <p className="no-data">No instructors found matching your search.</p>
      ) : (
        <div className="accordion-list">
          {filteredInstructors.map((instructor) => (
            <div
              key={instructor.id}
              className={`accordion-item ${expandedId === instructor.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => toggleAccordion(instructor.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{instructor.first_name} {instructor.last_name}</span>
                    <span className="course-category">ID: {instructor.id}</span>
                  </div>
                  <div className="meta-group">
                    <span className="instructor-badge">{instructor.email}</span>
                    <span className={`status-pill ${instructor.is_active ? 'active' : 'inactive'}`}>
                      {instructor.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="learner-count">
                      Joined {new Date(instructor.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    type="button"
                    className="btn-icon-toggle"
                    aria-label="Toggle Details"
                  >
                    {expandedId === instructor.id ? '−' : '+'}
                  </button>
                </div>
              </div>

              {expandedId === instructor.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>Instructor Profile</h5>
                      <p>Full Name: {instructor.first_name} {instructor.last_name}</p>
                      <p>Email: {instructor.email}</p>
                      <p>Phone: {instructor.phone || 'Not provided'}</p>
                      <p>Bio: {instructor.bio || 'No biography provided.'}</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">Joined</span>
                        <strong>{new Date(instructor.created_at).toLocaleDateString()}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Last Login</span>
                        <strong>
                          {instructor.last_login
                            ? new Date(instructor.last_login).toLocaleDateString()
                            : '—'}
                        </strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Role</span>
                        <strong>Instructor</strong>
                      </div>
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      className={instructor.is_active ? 'btn-warning' : 'btn-success'}
                      onClick={() => handleStatusToggle(instructor.id, instructor.is_active)}
                    >
                      {instructor.is_active ? 'Deactivate Instructor' : 'Activate Instructor'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderCourseAccordion = () => (
    <div className="course-accordion-container">
      {filteredCourses.length === 0 ? (
        <p className="no-data">No courses found matching your search.</p>
      ) : (
        <div className="accordion-list">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className={`accordion-item ${expandedId === course.id ? 'expanded' : ''}`}
            >
              <div className="accordion-header" onClick={() => toggleAccordion(course.id)}>
                <div className="header-info">
                  <div className="title-group">
                    <span className="course-title">{course.title}</span>
                    <span className="course-category">{course.category_name || 'General'}</span>
                  </div>
                  <div className="meta-group">
                    <span className={`status-pill active`}>
                      {course.enrollments} Learners
                    </span>
                    <span className="learner-count">
                      {course.completions} completions
                    </span>
                    <span className="course-revenue">
                      ${parseFloat(course.revenue || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button
                    type="button"
                    className="btn-icon-toggle"
                    aria-label="Toggle Details"
                  >
                    {expandedId === course.id ? '−' : '+'}
                  </button>
                </div>
              </div>

              {expandedId === course.id && (
                <div className="accordion-content">
                  <div className="content-grid">
                    <div className="description-section">
                      <h5>Performance Overview</h5>
                      <p>Avg. Rating: {course.avg_rating > 0 ? `${Number(course.avg_rating).toFixed(1)} / 5.0` : 'No reviews yet'}</p>
                      <p>Total Revenue: ${parseFloat(course.revenue || 0).toFixed(2)}</p>
                      <p>Completion Rate: {course.enrollments > 0 ? Math.round((course.completions / course.enrollments) * 100) : 0}%</p>
                    </div>
                    <div className="stats-section">
                      <div className="stat-item">
                        <span className="label">Enrollments</span>
                        <strong>{course.enrollments}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Completions</span>
                        <strong>{course.completions}</strong>
                      </div>
                      <div className="stat-item">
                        <span className="label">Revenue</span>
                        <strong>${parseFloat(course.revenue || 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="content-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/courses/${course.id}`)}
                    >
                      View Page
                    </button>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => navigate(`/edit-course/${course.id}`)}
                    >
                      Full Editor
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/courses/${course.id}/manage-announcements`)}
                    >
                      Announcements
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setSelectedCourse(course.id);
                        setActiveTab('analytics');
                      }}
                    >
                      Analytics
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) return <div className="loading">Loading...</div>;

  if (isAdmin) {
    return (
      <div className="page instructor-dashboard admin-view">
        <div className="container">
          <section className="instructor-hero admin">
            <div className="hero-content">
              <p className="eyebrow">Instructor console</p>
              <h1>Manage instructors</h1>
              <p>Review instructor activity, manage credentials, and toggle platform access.</p>
            </div>
            <div className="hero-stats">
              <div className="hero-stat-card">
                <span className="stat-label">Total instructors</span>
                <p className="stat-value">{totalInstructors}</p>
              </div>
              <div className="hero-stat-card">
                <span className="stat-label">Active</span>
                <p className="stat-value">{activeInstructors}</p>
              </div>
            </div>
          </section>

          <div className="course-management-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Management</p>
                <h2>Directory</h2>
              </div>
              <div className="search-bar-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {renderInstructorAccordion()}
          </div>
        </div>
      </div>
    );
  }

  const heroStats = [
    { label: 'Courses', value: summary?.courses?.total_courses || analytics?.stats?.total_courses || 0 },
    { label: 'Students', value: summary?.students?.total_students || analytics?.stats?.total_enrollments || 0 },
    { label: 'Completions', value: summary?.students?.course_completions || analytics?.stats?.completions || 0 },
    {
      label: 'Revenue',
      value: `$${Number(summary?.revenue?.total_revenue || analytics?.stats?.total_revenue || 0).toFixed(2)}`,
    },
  ];

  const tabs = [
    { id: 'courses', label: 'My Courses' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'students', label: 'Students' },
    { id: 'revenue', label: 'Revenue' },
  ];

  return (
    <div className="page instructor-dashboard">
      <div className="container">
        <section className="instructor-hero">
          <div className="hero-content">
            <p className="eyebrow">Instructor Console</p>
            <h1>Welcome back, {user?.first_name}!</h1>
            <p>Track course performance, enrollments, and revenue in one place.</p>
          </div>
          <div className="hero-stats">
            {heroStats.map((stat) => (
              <div key={stat.label} className="hero-stat-card">
                <span className="stat-label">{stat.label}</span>
                <p className="stat-value">{stat.value}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="instructor-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? 'active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'courses' && (
          <div className="instructor-grid">
            <div className="course-management-section">
              <div className="section-header-row">
                <div className="panel-header-simple">
                  <p className="label">Courses</p>
                  <h2>Performance</h2>
                </div>
                <div className="search-bar-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Link to="/create-course" className="btn-primary btn-small-cta">
                  + New Course
                </Link>
              </div>
              {renderCourseAccordion()}
            </div>

            <aside className="dashboard-section sidebar">
              <h2>Recent Enrollments</h2>
              {(summary?.recent_activity || analytics?.recent_enrollments || []).length === 0 ? (
                <p className="no-data">No recent enrollments</p>
              ) : (
                <div className="recent-enrollments-list">
                  {(summary?.recent_activity || analytics?.recent_enrollments || []).slice(0, 5).map((enrollment, idx) => (
                    <div key={enrollment.id || idx} className="enrollment-item">
                      <div className="student-info">
                        <span className="student-name">
                          {enrollment.user_name || `${enrollment.first_name} ${enrollment.last_name}`}
                        </span>
                        <span className="student-email">{enrollment.email}</span>
                      </div>
                      <div className="enrollment-info">
                        <span className="course-name">{enrollment.course_title}</span>
                        <span className="enrollment-date">
                          {new Date(enrollment.created_at || enrollment.enrolled_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <div className="analytics-controls">
              <select
                value={selectedCourse || ''}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                }}
              >
                <option value="">Select a course</option>
                {(analytics?.course_performance || []).map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            {courseAnalytics ? (
              <div className="analytics-grid">
                <div className="analytics-card">
                  <h3>Enrollment Stats</h3>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.enrollments?.total || 0}</span>
                      <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.enrollments?.completed || 0}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{Math.round(courseAnalytics.enrollments?.avg_progress || 0)}%</span>
                      <span className="stat-label">Avg Progress</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card">
                  <h3>Reviews</h3>
                  <div className="stats-row">
                    <div className="stat-item">
                      <span className="stat-value">{Number(courseAnalytics.reviews?.avg_rating || 0).toFixed(1)}</span>
                      <span className="stat-label">Avg Rating</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{courseAnalytics.reviews?.total_reviews || 0}</span>
                      <span className="stat-label">Reviews</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card full-width">
                  <h3>Lesson Completion</h3>
                  <div className="lesson-stats-list">
                    {(courseAnalytics.lesson_stats || []).map((lesson) => (
                      <div key={lesson.id} className="lesson-stat-item">
                        <span className="lesson-title">{lesson.title}</span>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${lesson.total_enrolled > 0 ? (lesson.completions / lesson.total_enrolled) * 100 : 0}%`
                            }}
                          />
                        </div>
                        <span className="completion-count">
                          {lesson.completions}/{lesson.total_enrolled}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-card full-width">
                  <h3>Quiz Performance</h3>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Quiz</th>
                          <th>Attempts</th>
                          <th>Avg Score</th>
                          <th>Pass Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(courseAnalytics.quiz_performance || []).map((quiz) => (
                          <tr key={quiz.id}>
                            <td>{quiz.title}</td>
                            <td>{quiz.attempts}</td>
                            <td>{Math.round(quiz.avg_score || 0)}%</td>
                            <td>{quiz.attempts > 0 ? Math.round((quiz.passed / quiz.attempts) * 100) : 0}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <p className="no-data">Select a course to view analytics</p>
            )}
          </div>
        )}

        {activeTab === 'students' && (
          <div className="students-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Students</p>
                <h2>Performance Overview</h2>
              </div>
              <select
                value={selectedCourse || ''}
                onChange={(e) => setSelectedCourse(e.target.value)}
              >
                <option value="">All Courses</option>
                {(analytics?.course_performance || []).map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="dashboard-section">
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Progress</th>
                      <th>Quiz Avg</th>
                      <th>Lessons</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">No students found</td>
                      </tr>
                    ) : (
                      students.map((student, idx) => (
                        <tr key={`${student.user_id}-${idx}`}>
                          <td>
                            <div>
                              <strong>{student.first_name} {student.last_name}</strong>
                              <br />
                              <small>{student.email}</small>
                            </div>
                          </td>
                          <td>{student.course_title}</td>
                          <td>
                            <div className="progress-cell">
                              <div className="progress-bar small">
                                <div
                                  className="progress-fill"
                                  style={{ width: `${student.progress_percentage || 0}%` }}
                                />
                              </div>
                              <span>{Math.round(student.progress_percentage || 0)}%</span>
                            </div>
                          </td>
                          <td>{Math.round(student.avg_quiz_score || 0)}%</td>
                          <td>{student.lessons_completed}/{student.total_lessons}</td>
                          <td>
                            <span className={`status-badge ${student.completed_at ? 'completed' : 'in-progress'}`}>
                              {student.completed_at ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="revenue-section">
            <div className="section-header-row">
              <div className="panel-header-simple">
                <p className="label">Earnings</p>
                <h2>Revenue Overview</h2>
              </div>
            </div>

            <div className="revenue-grid">
              <div className="revenue-card highlight">
                <h3>Total Revenue</h3>
                <p className="revenue-value">
                  ${Number(summary?.revenue?.total_revenue || analytics?.stats?.total_revenue || 0).toFixed(2)}
                </p>
                <span className="revenue-hint">All time earnings</span>
              </div>

              <div className="revenue-card">
                <h3>Last 30 Days</h3>
                <p className="revenue-value">
                  ${Number(summary?.revenue?.revenue_30d || 0).toFixed(2)}
                </p>
                <span className="revenue-hint">{summary?.revenue?.total_sales || 0} total sales</span>
              </div>

              <div className="revenue-card">
                <h3>Total Students</h3>
                <p className="revenue-value">
                  {summary?.students?.total_students || analytics?.stats?.total_enrollments || 0}
                </p>
                <span className="revenue-hint">{summary?.students?.new_students_30d || 0} new this month</span>
              </div>

              <div className="revenue-card">
                <h3>Average Rating</h3>
                <p className="revenue-value">
                  {Number(summary?.ratings?.avg_rating || 0).toFixed(1)}
                </p>
                <span className="revenue-hint">{summary?.ratings?.total_reviews || 0} reviews</span>
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Revenue by Course</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Enrollments</th>
                      <th>Completions</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.course_performance || []).length === 0 ? (
                      <tr><td colSpan="4" className="no-data">No courses found</td></tr>
                    ) : (
                      (analytics?.course_performance || []).map((course) => (
                        <tr key={course.id}>
                          <td>{course.title}</td>
                          <td>{course.enrollments}</td>
                          <td>{course.completions}</td>
                          <td className="revenue-cell">${parseFloat(course.revenue || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dashboard-section">
              <h3>Recent Transactions</h3>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Amount</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.recent_payments || []).length === 0 ? (
                      <tr><td colSpan="4" className="no-data">No transactions found</td></tr>
                    ) : (
                      (analytics?.recent_payments || []).map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <div>
                              <strong>{payment.first_name} {payment.last_name}</strong>
                              <br />
                              <small>{payment.email}</small>
                            </div>
                          </td>
                          <td>{payment.course_title}</td>
                          <td className="revenue-cell">${parseFloat(payment.amount || 0).toFixed(2)}</td>
                          <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorDashboard;
